import React from 'react';
import classNames from 'classnames/bind';
import { CellProps, Column } from 'react-table';
import { useNavigate, useParams } from 'react-router-dom';

import { Camera, getCameraStatusHelpText } from 'types/camera';
import { getStreamStatusHelpText, Stream } from 'types/stream';
import { NavRoute, DeployRoute } from 'application/types/routes';

import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useTagsFilterParams } from 'tags/hooks/useTagsFilterParams';
import {
  UseVideoSourcesParams,
  VideoSource,
  useVideoSources,
} from 'hooks/api/useVideoSources';
import { useStreams } from 'streams/hooks/useStreams';
import { OffsetPaginationParams } from 'types/pagination';

import { Checkbox } from 'components/Checkbox/Checkbox';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { IconButton } from 'components/IconButton/IconButton';
import { PaginatedTable } from 'components/Table/PaginatedTable';
import { QuerySearch } from 'components/QuerySearch/QuerySearch';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { StatusIndicator } from 'components/StatusIndicator/StatusIndicator';
import { TableLabelsCell } from 'components/Table/Table';
import { Text } from 'components/Text/Text';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { VideoSourceSnapshot } from 'components/VideoSourceSnapshot/VideoSourceSnapshot';

import { useRapidDeployState } from '../hooks/useRapidDeployState';
import styles from '../RapidDeploy.module.scss';

const c = classNames.bind(styles);

type PreviewSourceResult = {
  previewSource: VideoSource | null;
  isVirtualCameraWithoutUploads: boolean;
  camera: Camera | undefined;
};

/**
 * Hook to compute preview source for a video source.
 * For virtual cameras, fetches and uses the latest file stream.
 */
function usePreviewSource(original: VideoSource): PreviewSourceResult {
  const isVirtualCamera =
    !('stream_type' in original) && original.conn_type === 'virtual';

  // Fetch file streams for virtual cameras
  const { data: fileStreams, isSuccess: hasLoadedStreams } = useStreams({
    params: {
      camera_ids: isVirtualCamera ? [original.id] : [],
      stream_types: ['file'],
      limit: 1,
    },
    enabled: isVirtualCamera,
    keepPreviousData: true,
  });

  // Find the latest file stream for virtual cameras
  const latestStream = React.useMemo(() => {
    if (!isVirtualCamera || !fileStreams) return null;
    return fileStreams.data.reduce<Stream | null>((latest, stream) => {
      if (!latest) return stream;
      return new Date(stream.created_at).getTime() >
        new Date(latest.created_at).getTime()
        ? stream
        : latest;
    }, null);
  }, [isVirtualCamera, fileStreams]);

  // Compute preview source similar to CameraDetail.tsx
  let previewSource: VideoSource | null = null;
  let camera: Camera | undefined = undefined;
  let isVirtualCameraWithoutUploads = false;

  if ('stream_type' in original) {
    // If it's already a stream, use it directly
    previewSource = original;
  } else if (isVirtualCamera) {
    // For virtual cameras, use the latest file stream
    camera = original;
    previewSource = latestStream ?? null;
    isVirtualCameraWithoutUploads = hasLoadedStreams && latestStream === null;
  } else {
    // For other cameras, use the camera itself
    previewSource = original;
  }

  return {
    previewSource,
    isVirtualCameraWithoutUploads,
    camera,
  };
}

export const RAPID_DEPLOY_VIDEO_SOURCES_PAGE_SIZE = 20;

export type RapidDeployVideoSourcesListProps = {
  filters: UseVideoSourcesParams;
};

export function RapidDeployVideoSourcesList({
  filters: baseFilters,
}: RapidDeployVideoSourcesListProps) {
  const { data: currentPlan, isSuccess } = useCurrentPlan();
  const canCreateTags =
    (isSuccess && currentPlan === null) ||
    Boolean(currentPlan?.can_create_tags);
  const tagFilters = useTagsFilterParams();

  React.useEffect(() => {
    setFilters((filters) => ({
      ...filters,
      ...baseFilters,
      ...tagFilters,
      page: 1,
    }));
  }, [tagFilters, baseFilters]);

  const [filters, setFilters] = React.useState<
    UseVideoSourcesParams & OffsetPaginationParams
  >({
    ...tagFilters,
    ...baseFilters,
    page: 1,
    limit: RAPID_DEPLOY_VIDEO_SOURCES_PAGE_SIZE,
  });
  const queryResult = useVideoSources({
    params: filters,
    keepPreviousData: true,
  });

  const [searchFilters, setSearchFilters] =
    React.useState<UseVideoSourcesParams>({});
  const searchQueryResult = useVideoSources({
    params: { ...baseFilters, ...searchFilters, limit: 20 },
    enabled: Object.keys(searchFilters).length > 0,
    keepPreviousData: false,
  });

  function handleSearchInput(value?: string) {
    if (!value) {
      setSearchFilters({});
      setFilters(({ names, ...filters }) => ({ ...filters, page: 1 }));
      return;
    }

    setSearchFilters({
      names: [value],
    });
  }

  const { isLoading } = queryResult;
  const { dispatchVideoSources } = useRapidDeployState();

  // Collect virtual camera IDs from current page for row click validation
  const virtualCameraIds = React.useMemo(() => {
    return (
      queryResult.data?.data
        .filter((s): s is Camera => !('stream_type' in s))
        .filter((c) => c.conn_type === 'virtual')
        .map((c) => c.id) ?? []
    );
  }, [queryResult.data?.data]);

  // Fetch streams for virtual cameras to check if they have uploads
  const { data: virtualCameraStreams } = useStreams({
    params: {
      camera_ids: virtualCameraIds,
      stream_types: ['file'],
      limit: 1,
    },
    enabled: virtualCameraIds.length > 0,
    keepPreviousData: true,
  });

  // Create a map of virtual camera ID -> has uploads
  const virtualCameraHasUploads = React.useMemo(() => {
    const map = new Map<string, boolean>();
    if (!virtualCameraStreams) return map;
    const cameraIdsWithStreams = new Set(
      virtualCameraStreams.data
        .map((s) => s.camera_id)
        .filter((id): id is string => Boolean(id))
    );
    virtualCameraIds.forEach((id) => {
      map.set(id, cameraIdsWithStreams.has(id));
    });
    return map;
  }, [virtualCameraStreams, virtualCameraIds]);

  const columns = React.useMemo(() => {
    const columns: Column<VideoSource>[] = [
      {
        id: 'select',
        Cell: SelectCell,
        width: 0,
      },
      {
        id: 'snapshot',
        Cell: ThumbnailCell,
        width: 0,
      },
      {
        id: 'status',
        Cell({ row: { original } }: CellProps<VideoSource>) {
          const tooltipText =
            'conn_type' in original
              ? getCameraStatusHelpText(original as Camera)
              : getStreamStatusHelpText(original as Stream);
          return (
            <StatusIndicator
              status={original.status}
              size="small"
              tooltip={tooltipText}
            />
          );
        },
        width: 0,
      },
      {
        id: 'name',
        Cell: NameCell,
      },
    ];

    if (canCreateTags) {
      columns.push({
        id: 'tags',
        Cell(props: CellProps<VideoSource>) {
          const sourceType =
            'stream_type' in props.row.original ? 'stream' : 'camera';
          return (
            <TableLabelsCell
              {...props}
              entity={`${sourceType}s`}
              singleQueryKey={[sourceType]}
              readOnly
            />
          );
        },
      });
    }

    return columns;
  }, [canCreateTags]);

  function handleApplyFilters() {
    setFilters((filters) => ({ ...filters, ...searchFilters, page: 1 }));
  }

  return (
    <>
      <QuerySearch
        entityName="video source"
        searchLabel="Search video sources..."
        searchResultField="name"
        searchQueryResult={searchQueryResult}
        onApplyFilters={handleApplyFilters}
        onSuggestionSelect={(data) =>
          dispatchVideoSources({ type: 'add', data })
        }
        onValueChange={handleSearchInput}
        totalElements={queryResult.data?.total_elements}
      />

      {isLoading ? (
        <div className={c('loader-table')}>
          <SkeletonList min={8} max={15} component={<LoaderRow />} />
        </div>
      ) : (
        <PaginatedTable<VideoSource>
          className={c('table')}
          id="rapid-deploy-video-sources"
          label="Video sources"
          queryResult={queryResult}
          columns={columns}
          pageSize={RAPID_DEPLOY_VIDEO_SOURCES_PAGE_SIZE}
          page={filters.page}
          onPageChange={(page) =>
            setFilters((filters) => ({ ...filters, page }))
          }
          onRowClick={(data) => {
            const isVirtualCamera =
              !('stream_type' in data) && data.conn_type === 'virtual';
            if (isVirtualCamera) {
              // Check if this virtual camera has uploads
              const hasUploads = virtualCameraHasUploads.get(data.id);
              // Only allow selection if it has uploads (undefined means streams haven't loaded yet, so prevent)
              if (hasUploads !== true) {
                return;
              }
            }
            dispatchVideoSources({ type: 'toggle', data });
          }}
        />
      )}
    </>
  );
}

function SelectCell({ row: { original } }: CellProps<VideoSource>) {
  const { videoSources, dispatchVideoSources } = useRapidDeployState();
  const { isVirtualCameraWithoutUploads } = usePreviewSource(original);

  const checkbox = (
    <Checkbox
      id={`${'stream_type' in original ? 'stream' : 'camera'}_${original.id}`}
      value={original.id}
      name="rapid-deploy-sources"
      checked={videoSources.some(({ id }) => id === original.id)}
      disabled={isVirtualCameraWithoutUploads}
      onChange={(event) => {
        if (event.target.checked) {
          dispatchVideoSources({ type: 'add', data: original });
          return;
        }

        dispatchVideoSources({ type: 'remove', data: original });
      }}
    />
  );

  if (isVirtualCameraWithoutUploads) {
    return (
      <Tooltip content="Deploying analytics to a Virtual camera requires atleast one uploaded clip. Please wait for a clip to be uploaded and then retry">
        {checkbox}
      </Tooltip>
    );
  }

  return checkbox;
}

function ThumbnailCell({ row: { original } }: CellProps<VideoSource>) {
  const { previewSource, isVirtualCameraWithoutUploads, camera } =
    usePreviewSource(original);

  // Render custom placeholder for virtual cameras without streams
  if (isVirtualCameraWithoutUploads) {
    return (
      <div className={c('snapshot', 'placeholder-wrap')}>
        <Text className={c('snapshot', 'placeholder-text')} size="small">
          No camera uploads found
        </Text>
      </div>
    );
  }

  return (
    <VideoSourceSnapshot
      className={c('snapshot')}
      source={previewSource}
      camera={camera}
      compact
      hideTitle
    />
  );
}

function NameCell({ row: { original } }: CellProps<VideoSource>) {
  const navigate = useNavigate();
  const { applicationID } = useParams();
  const { isVirtualCameraWithoutUploads } = usePreviewSource(original);
  const sourceType = 'stream_type' in original ? 'stream' : 'camera';
  const isVirtualCamera =
    !('stream_type' in original) && original.conn_type === 'virtual';

  function handleLabelClick(event: React.MouseEvent<HTMLLabelElement>) {
    if (isVirtualCameraWithoutUploads) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  function handleGearClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (applicationID) {
      navigate(
        `/applications/${applicationID}/${NavRoute.DEPLOY}/${DeployRoute.CAMERAS}/${original.id}`,
        {
          state: {
            tab: 'settings',
          },
        }
      );
    }
  }

  const referencePipelineCount = isVirtualCamera
    ? new Set((original as Camera).reference_pipeline_ids).size
    : 0;

  return (
    <label
      className={c('table-cell', {
        disabled: isVirtualCameraWithoutUploads,
      })}
      htmlFor={
        isVirtualCameraWithoutUploads
          ? undefined
          : `${sourceType}_${original.id}`
      }
      onClick={handleLabelClick}
      style={
        isVirtualCameraWithoutUploads ? { cursor: 'not-allowed' } : undefined
      }
    >
      <Heading level="4" asChild>
        <strong className={c('source-name-heading')}>
          <Icon name={sourceType} />
          <span>{original.name}</span>
          {isVirtualCameraWithoutUploads && (
            <Tooltip
              content="Deploying analytics to a Virtual camera requires atleast one uploaded clip.
                       Please wait for the camera to upload a clip and then retry."
            >
              <span className={c('warning-icon')}>
                <Icon name="warning" className="theme danger" />
              </span>
            </Tooltip>
          )}
          {isVirtualCamera && (
            <Tooltip content="View camera workflows">
              <IconButton
                icon="settings"
                variant="secondary"
                size="sm"
                onClick={(e) =>
                  handleGearClick(e as React.MouseEvent<HTMLButtonElement>)
                }
              />
            </Tooltip>
          )}
        </strong>
      </Heading>

      {'conn_type' in original && (
        <>
          <Text className={c('table-cell-properties')} inline>
            {original.model && <span>{original.model}</span>}
            {(original.ip_ext || original.ip_local) && (
              <span>
                <Icon name="global" />
                <span>{original.ip_ext ?? original.ip_local}</span>
              </span>
            )}
          </Text>
          {isVirtualCamera && referencePipelineCount > 0 && (
            <Text
              className={c('table-cell-properties', 'reference-count')}
              inline
            >
              <span>
                {referencePipelineCount} existing workflow
                {referencePipelineCount !== 1 ? 's' : ''}
              </span>
            </Text>
          )}
        </>
      )}
    </label>
  );
}

function LoaderRow() {
  return (
    <div className={c('loader-table-row')}>
      <div className={c('loader-table-cell', 'auto')}>
        <span className={c('loader-radio')} />
      </div>

      <div className={c('loader-table-cell', 'auto')}>
        <span className={c('loader-snapshot', 'snapshot', 'skeleton-image')} />
      </div>

      <div className={c('loader-table-cell', 'auto')}>
        <span className={c('skeleton-bar', 'loader-status-indicator')} />
      </div>

      <div className={c('loader-table-cell')}>
        <Heading level="4" asChild>
          <span className="skeleton-text" />
        </Heading>
      </div>
    </div>
  );
}
